import { tokenize, variableLabel, type TemplateVariableKind } from '@/lib/templates';
import { cn } from '@/lib/utils';

const CHIP: Record<TemplateVariableKind, string> = {
  builtin: 'border-border bg-muted text-muted-foreground',
  contact: 'border-primary/40 bg-accent text-accent-foreground',
  custom: 'border-foreground/30 bg-card text-foreground',
};

const TITLE: Record<TemplateVariableKind, string> = {
  builtin: 'Filled automatically from the job, the clock or the sender',
  contact: 'Personalized for each recipient from their contact record',
  custom: 'Asked for when the template is used',
};

/** Message text with its `{{variables}}` drawn as small chips, coloured by who fills them in. */
export function TokenText({ body, className, plain }: { body: string; className?: string; /** Show the raw token instead of its label. */ plain?: boolean }) {
  return (
    <span className={cn('whitespace-pre-line', className)}>
      {tokenize(body).map((part, i) =>
        part.type === 'text' ? (
          <span key={i}>{part.value}</span>
        ) : (
          <span key={i} className={cn('mx-px inline-flex max-w-full items-baseline border px-1 align-baseline font-mono text-[0.8em] leading-[1.35]', CHIP[part.kind])} title={`${part.raw} · ${TITLE[part.kind]}${part.fallback ? ` · “${part.fallback}” when empty` : ''}`}>
            {plain ? part.raw : variableLabel(part.key)}
          </span>
        ),
      )}
    </span>
  );
}

/** Chip for one variable outside running text (tables, legends). */
export function VariableChip({ kind, children, title, className }: { kind: TemplateVariableKind; children: React.ReactNode; title?: string; className?: string }) {
  return (
    <span className={cn('inline-flex items-center gap-1 border px-1.5 py-0.5 font-mono text-[11px] whitespace-nowrap', CHIP[kind], className)} title={title ?? TITLE[kind]}>
      {children}
    </span>
  );
}
